import { Icon, Tooltip } from '@dixa/lib-shared-habla';
import * as Popover from '@radix-ui/react-popover';
import classnames from 'classnames';
import { SearchUserEnriched } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { ReactNode, RefObject, useEffect, useMemo, useRef } from 'react';

import { AwayStatus } from 'apis/presence';
import { CustomAwayStatus } from 'components/presence/components/customAwayStatus';
import { getPresenceIndicator, getStatus } from 'components/presence/utils';
import { getUserDisplayName } from 'utils/user';

import styles from './presenceListItem.module.scss';

/** Matches a row, so the presence popover can tell clicks on rows apart. */
export const presenceRowSelector = '[data-presence-row]';

/** The row the presence popover is open on, shared by every row in the list. */
export type PresenceRowSelection = {
  /** Key of the selected row, `${sectionId}:${memberId}`. */
  rowKey: string | undefined;
  onRowClick: (rowKey: string, memberId: string) => void;
  /** Called when the selected row unmounts. */
  onRowRemoved: (rowKey: string) => void;
};

type PresenceListItemProps = {
  /** Tells apart the rows of an agent listed in several sections. */
  sectionId: string;
  member: SearchUserEnriched;
  ownProfile: User;
  awayStatuses: AwayStatus[];
  selection: PresenceRowSelection;
  action?: ReactNode;
};

type SelectedRowAnchorProps = {
  row: RefObject<HTMLLIElement>;
  rowKey: string;
  onRemoved: (rowKey: string) => void;
};

// Positions the popover next to the row, and closes it when the row goes away,
// e.g. when the agent changes status and moves to another section. It sits
// beside the row rather than around it, so selecting a row doesn't remount it.
const SelectedRowAnchor = ({
  row,
  rowKey,
  onRemoved,
}: SelectedRowAnchorProps): JSX.Element => {
  useEffect(() => () => onRemoved(rowKey), [rowKey, onRemoved]);

  return <Popover.Anchor virtualRef={row} />;
};

export const PresenceListItem = ({
  sectionId,
  member,
  ownProfile,
  awayStatuses,
  selection,
  action,
}: PresenceListItemProps): JSX.Element => {
  const isOwnAgent = ownProfile.id === member.id;
  const status = getStatus(member);
  const { icon, tooltip } = getPresenceIndicator(member);

  const isWorking = status === 'working';
  const isAway = status === 'away';
  const isOffline = status === 'offline';

  const rootClass = classnames({
    [styles.root]: true,
    [styles.rootOnline]: isWorking,
    [styles.rootAway]: isAway,
    [styles.rootOffline]: isOffline,
  });

  const customAwayStatus = useMemo(() => {
    if (!isAway || !awayStatuses.length) {
      return null;
    }

    const awayStatus = awayStatuses.find(
      ({ id }) => id === member.custom_away_status?.custom_away_status_id,
    );

    if (!awayStatus) {
      return null;
    }

    return (
      <CustomAwayStatus
        awayStatus={awayStatus}
        statusUpdatedAt={member.custom_away_status?.status_updated_at || null}
      />
    );
  }, [isAway, member.custom_away_status, awayStatuses]);

  const memberName = getUserDisplayName(member);
  const rowKey = `${sectionId}:${member.id}`;
  const rowRef = useRef<HTMLLIElement>(null);

  const handleClick = () => {
    ampli.presenceListItemClicked();
    selection.onRowClick(rowKey, member.id);
  };

  return (
    <>
      <li
        ref={rowRef}
        className={rootClass}
        onClick={handleClick}
        data-presence-row
      >
        <div className={styles.container}>
          <Tooltip className={styles.iconTooltip} content={tooltip}>
            <Icon name={icon} className={styles.icon} size={12} />
          </Tooltip>
          <span className={styles.name}>
            {memberName} {isOwnAgent ? ' (you)' : ''}
          </span>
          {customAwayStatus}
          {action && <span className={styles.action}>{action}</span>}
        </div>
      </li>
      {rowKey === selection.rowKey && (
        <SelectedRowAnchor
          row={rowRef}
          rowKey={rowKey}
          onRemoved={selection.onRowRemoved}
        />
      )}
    </>
  );
};
